import { useCallback, useEffect, useRef, useState } from 'react'
import { encodeWav } from '@/lib/wav'
import { canAskForMicrophone, classifyMicFailure, policyAllowsMicrophone, type MicFailure } from '@/lib/microphone'

/**
 * THE MICROPHONE, ONCE — shared by both recorders (Build 10b).
 *
 * Moved out of src/routes/Record.tsx (Foredesk's SMS-link record page) when
 * Kelvo's Questions page needed to record too, so the two can never drift on
 * the details below — each of which was learned the hard way (build standard
 * §16):
 *
 * ==== WHY ScriptProcessorNode, WHICH IS DEPRECATED ====
 * Because AudioWorklet — its replacement — produces INTERMITTENT SILENCE on
 * Chrome/macOS, reproduced across three separate implementations including
 * Speechmatics' own library. Silence is the worst possible failure here: the
 * upload succeeds, the transcript comes back empty, and it presents as "the
 * transcription is rubbish" rather than "the recording never captured
 * anything". ScriptProcessorNode is deprecated and completely reliable for
 * short clips, and main-thread processing is a non-issue for a minute of speech.
 *
 * ==== THE NATIVE RATE, RESAMPLED IN SOFTWARE ====
 * Forcing AudioContext({ sampleRate: 16000 }) produces silence on some
 * hardware; src/lib/wav.ts resamples instead.
 *
 * ==== THE RED DOT GOES WHEN IT STOPS ====
 * Every track is stopped on stop, on cancel and on unmount. A live track keeps
 * the browser's recording indicator on, which reads as "this thing is still
 * listening to me".
 *
 * The hook records and hands back WAV bytes. What happens to them — an upload
 * on a link token, or on the owner's session — is the caller's business.
 */
export function useWavRecorder(options: {
  /** Hard stop: the recording ends itself at this many seconds. */
  maxSeconds: number
  /** Called once per recording with the finished WAV. */
  onFinished: (wav: ArrayBuffer) => void
}) {
  const { maxSeconds } = options
  const onFinishedRef = useRef(options.onFinished)
  onFinishedRef.current = options.onFinished

  // Null when not recording; otherwise the seconds so far.
  const [seconds, setSeconds] = useState<number | null>(null)

  // Capture plumbing, held in refs so a re-render never drops a live audio graph.
  const contextRef = useRef<AudioContext | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const samplesRef = useRef<Float32Array[]>([])
  const tickRef = useRef<number | null>(null)

  /** Tear the audio graph down completely. Safe to call twice. */
  const teardown = useCallback(() => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current)
      tickRef.current = null
    }
    processorRef.current?.disconnect()
    sourceRef.current?.disconnect()
    streamRef.current?.getTracks().forEach((t) => t.stop())
    void contextRef.current?.close().catch(() => undefined)
    processorRef.current = null
    sourceRef.current = null
    streamRef.current = null
    contextRef.current = null
  }, [])

  useEffect(() => teardown, [teardown])

  /** Stop and hand back the recording. Does nothing if not recording. */
  const stop = useCallback(() => {
    if (!contextRef.current) return
    const sampleRate = contextRef.current.sampleRate
    const collected = samplesRef.current
    samplesRef.current = []
    teardown()
    setSeconds(null)
    onFinishedRef.current(encodeWav(collected, sampleRate))
  }, [teardown])

  // The interval below calls the CURRENT stop, never the one it closed over.
  const stopRef = useRef(stop)
  stopRef.current = stop

  /** Stop and throw the recording away. */
  const cancel = useCallback(() => {
    samplesRef.current = []
    teardown()
    setSeconds(null)
  }, [teardown])

  /** Start recording. Null when it started; otherwise WHY it did not
   *  (src/lib/microphone.ts), so the screen can name the cause rather than
   *  send the person to fix a setting that was never the problem. */
  const start = useCallback(async (): Promise<MicFailure | null> => {
    samplesRef.current = []
    if (!canAskForMicrophone()) return 'unsupported'
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      streamRef.current = stream

      const ctx = new AudioContext()
      // iOS Safari starts contexts suspended until a user gesture resumes them.
      if (ctx.state === 'suspended') await ctx.resume()
      contextRef.current = ctx

      const source = ctx.createMediaStreamSource(stream)
      const processor = ctx.createScriptProcessor(4096, 1, 1)
      processor.onaudioprocess = (event) => {
        // COPY the buffer. The underlying Float32Array is reused by the audio
        // engine on the next callback, so keeping the reference would give a
        // recording where every frame is the last frame.
        samplesRef.current.push(new Float32Array(event.inputBuffer.getChannelData(0)))
      }
      source.connect(processor)
      // Connecting to the destination is REQUIRED for onaudioprocess to fire at
      // all — without it the callback silently never runs and the recording is
      // empty. It contributes no audible output because the processor writes
      // nothing to its output buffer.
      processor.connect(ctx.destination)
      sourceRef.current = source
      processorRef.current = processor

      setSeconds(0)
      tickRef.current = window.setInterval(() => {
        setSeconds((s) => {
          if (s === null) return s
          const next = s + 1
          if (next >= maxSeconds) {
            // Deferred so the stop does not run inside a state update.
            window.setTimeout(() => stopRef.current(), 0)
            return maxSeconds
          }
          return next
        })
      }, 1000)
      return null
    } catch (e) {
      teardown()
      setSeconds(null)
      return classifyMicFailure(e, policyAllowsMicrophone())
    }
  }, [maxSeconds, teardown])

  return { recording: seconds !== null, seconds: seconds ?? 0, start, stop, cancel }
}
